import { Close } from '@mui/icons-material'
import { IconButton, List, Stack, Typography } from '@mui/material'
import { useTranslate } from '@tolgee/react'
import { useYardStores } from '@yard/AppProvider'
import { observer } from 'mobx-react-lite'
import { FC, useMemo } from 'react'
import { HousekeepingPlanAccumulatorViewStore } from '../../../stores/housekeeping-plan-accumulator.view-store'
import { AccumulatorListHeader } from './AccumulatorListHeader'
import { AccumulatorRow } from './AccumulatorRow'
import { formatAccumulatorPrimary } from './format-accumulator-primary'

interface Props {
  viewStore: HousekeepingPlanAccumulatorViewStore
}

export const HousekeepingPlanAccumulator: FC<Props> = observer(({ viewStore }) => {
  const { t } = useTranslate()
  const { journeyStore } = useYardStores()

  const containerById = useMemo(() => {
    const map = new Map<number, { containerNumber?: string; isoCode?: string }>()
    if (viewStore.accumulatedCount === 0) return map
    journeyStore.items.forEach(j => {
      if (j.container) {
        map.set(j.containerId, {
          containerNumber: j.container.containerNumber,
          isoCode: j.container.isoCode ?? undefined,
        })
      }
    })
    return map
  }, [journeyStore.items, viewStore.accumulatedCount])

  const removeLabel = t('removeFromGroup', 'Remove from group')
  const toggleLabel = t('toggleSelected', 'Toggle selected')

  return (
    <Stack p={2} spacing={2} height='100%'>
      <Stack direction='row' alignItems='center' justifyContent='space-between'>
        <Typography variant='h3'>{t('housekeepingPlan', 'Housekeeping plan')}</Typography>
        <IconButton
          size='small'
          aria-label={t('close', 'Close')}
          onClick={() => viewStore.deactivate()}
        >
          <Close fontSize='small' />
        </IconButton>
      </Stack>
      <AccumulatorListHeader viewStore={viewStore} />
      {viewStore.accumulatedCount === 0 ? (
        <Typography variant='body2' color='text.secondary'>
          {t(
            'housekeepingPlanAccumulating',
            'Click containers in the yard to add them to this group.',
          )}
        </Typography>
      ) : (
        <List dense sx={{ overflowY: 'auto', flexGrow: 1 }}>
          {Array.from(viewStore.accumulated).map(id => {
            const info = containerById.get(id)
            const primary = formatAccumulatorPrimary(info, id, t)
            return (
              <AccumulatorRow
                key={id}
                primary={primary}
                secondary={info?.isoCode}
                checked={viewStore.isChecked(id)}
                onToggleChecked={() => viewStore.toggleChecked(id)}
                onRemove={() => viewStore.remove(id)}
                removeLabel={removeLabel}
                toggleLabel={toggleLabel}
              />
            )
          })}
        </List>
      )}
    </Stack>
  )
})
